import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDateOnly, residentStatusLabel, residentStatusTone } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { RowActions } from "@/components/app/patterns/row-actions";
import { Badge } from "@/components/ui/badge";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { BilledResident } from "@/components/app/payments/billed-resident";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";

type TrustFundRow = Output<typeof orpcClient.financials.reports.trustFunds>["items"][number];

/** Hoisted: the columns close over nothing, and a table is not a component. */
const COLUMNS: ReadonlyArray<ReportColumn<TrustFundRow>> = [
  {
    header: "Resident",
    cell: (row) => <BilledResident name={row.residentName} residentId={row.residentId} />,
    value: (row) => row.residentName,
  },
  { header: "Room", cell: (row) => row.roomNumber ?? "-", value: (row) => row.roomNumber },
  {
    header: "Status",
    cell: (row) =>
      row.residentStatus ? (
        <Badge variant={residentStatusTone(row.residentStatus)}>
          {residentStatusLabel(row.residentStatus)}
        </Badge>
      ) : (
        "-"
      ),
    value: (row) => row.residentStatus ?? "",
  },
  {
    header: "Last transaction",
    cell: (row) =>
      row.lastTransactionDate ? (
        formatDateOnly(row.lastTransactionDate)
      ) : (
        <span className="text-muted-foreground">Never</span>
      ),
    value: (row) => row.lastTransactionDate,
  },
  { header: "Balance", cell: (row) => row.balance, align: "right" },
  {
    header: "",
    cell: (row) => (
      <RowActions
        label={row.residentName}
        primary={{
          label: "Open trust fund",
          link: { to: "/residents/$residentId/trust-fund", params: { residentId: row.residentId } },
        }}
        items={[]}
      />
    ),
    value: () => "",
    align: "right",
  },
];

export const Route = createFileRoute("/_authenticated/_financials/trust-fund-report/")({
  component: TrustFunds,
});

/** Money the facility holds that belongs to residents. */
function TrustFunds() {
  const { facilityId } = useFacility();
  const report = useQuery(
    orpc.financials.reports.trustFunds.queryOptions({ input: { facilityId } }),
  );

  const rows = report.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Trust fund report"
        meta="Money held on residents' behalf. It is theirs, not the facility's."
      />

      {report.data ? (
        <StatTiles>
          <StatTile label="Held in trust" value={report.data.total} hint="Across every account" />
          <StatTile
            label="Residents with an account"
            value={report.data.items.length}
            hint={
              report.data.heldForDeparted.length
                ? `${report.data.heldForDeparted.length} no longer in the building`
                : "All still in the building"
            }
          />
        </StatTiles>
      ) : null}

      {report.data && report.data.heldForDeparted.length > 0 ? (
        <Alert>
          <AlertTitle>Held for residents who have left</AlertTitle>
          <AlertDescription>
            {report.data.heldForDeparted.length === 1
              ? "1 resident no longer in the building still has money here."
              : `${report.data.heldForDeparted.length} residents no longer in the building still have money here.`}{" "}
            It has to go back to them or their representative.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* A failed read is not an empty report. */}
      {report.isError ? (
        <ReadFailed
          message="Trust fund balances could not be loaded."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : (
        <ReportTable
          rows={rows}
          pending={report.isPending}
          filename="trust-funds.csv"
          noun="residents"
          empty={{
            title: "Nothing held",
            description:
              "Accounts appear here once a resident's trust fund is opened from their chart.",
          }}
          columns={COLUMNS}
        />
      )}
    </>
  );
}
